import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router';
import { UpdatedSchoolParams } from '#server/models';
import { useAdminData } from '@/hooks';
import { Loader } from '@/components/Common';
import { TRDParams } from './TRDParams';
import { UCParams } from './UCParams';
import { HDRParams } from './HDRParams';

export interface TRDData {
  trdMarkingHours: number;
  trdPreparationHours: number;
  trdConsultationHours: number;
  trdModerationHours: number;
}

export interface UCData {
  unitCoordinationAllocationMethod: string;
  unitCoordinationSmallUnitsStudents: number;
  unitCoordinationSmallUnitsHours: number;
  unitCoordinationMediumUnitsStudents: number;
  unitCoordinationMediumUnitsHours: number;
  unitCoordinationLargeUnitsHours: number;
  unitCoordinationFixedTotalHours: number;
  unitCoordinationFixedTotalMinutesStudent: number;
}

export interface HDRData {
  hdrSupervisorAllowance: number;
  hdrThesisChairAllowance: number;
}

export const ModifySchoolParameters = () => {
  const { setUpdatedSchoolParams } = useOutletContext<{
    setUpdatedSchoolParams: React.Dispatch<React.SetStateAction<UpdatedSchoolParams>>;
  }>();
  const [trdData, setTrdData] = useState<TRDData>({
    trdMarkingHours: 0,
    trdPreparationHours: 0,
    trdConsultationHours: 0,
    trdModerationHours: 0,
  });
  const [ucData, setUcData] = useState<UCData>({
    unitCoordinationAllocationMethod: '',
    unitCoordinationSmallUnitsStudents: 0,
    unitCoordinationSmallUnitsHours: 0,
    unitCoordinationMediumUnitsStudents: 0,
    unitCoordinationMediumUnitsHours: 0,
    unitCoordinationLargeUnitsHours: 0,
    unitCoordinationFixedTotalHours: 0,
    unitCoordinationFixedTotalMinutesStudent: 0,
  });
  const [hdrData, setHdrData] = useState<HDRData>({
    hdrSupervisorAllowance: 0,
    hdrThesisChairAllowance: 0,
  });
  const [largeUnitsStudents, setLargeUnitsStudents] = useState<number>(0);
  const { schoolParams, isPageLoading } = useAdminData();

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const category = e.target.dataset.category as string;
    const fieldName = e.target.dataset.key as string;
    const value = Number(e.target.value);
    if (isNaN(value)) {
      return;
    }

    switch (category) {
      case 'trd':
        setTrdData((prev) => ({
          ...prev,
          [fieldName]: value,
        }));
        break;
      case 'uc':
        if (fieldName === 'unitCoordinationMediumUnitsStudents') {
          setLargeUnitsStudents(value);
        }
        setUcData((prev) => ({
          ...prev,
          [fieldName]: value,
        }));
        break;
      case 'hdr':
        setHdrData((prev) => ({
          ...prev,
          [fieldName]: value,
        }));
        break;
    }

    setUpdatedSchoolParams((prev: any) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  /** Set default values */
  useEffect(() => {
    if (!schoolParams) {
      return;
    }
    setTrdData({
      trdMarkingHours: schoolParams.trdMarkingHours,
      trdPreparationHours: schoolParams.trdPreparationHours,
      trdConsultationHours: schoolParams.trdConsultationHours,
      trdModerationHours: schoolParams.trdModerationHours,
    });
    setUcData({
      unitCoordinationAllocationMethod: schoolParams.unitCoordinationAllocationMethod,
      unitCoordinationSmallUnitsStudents: schoolParams.unitCoordinationSmallUnitsStudents,
      unitCoordinationSmallUnitsHours: schoolParams.unitCoordinationSmallUnitsHours,
      unitCoordinationMediumUnitsStudents: schoolParams.unitCoordinationMediumUnitsStudents,
      unitCoordinationMediumUnitsHours: schoolParams.unitCoordinationMediumUnitsHours,
      unitCoordinationLargeUnitsHours: schoolParams.unitCoordinationLargeUnitsHours,
      unitCoordinationFixedTotalHours: schoolParams.unitCoordinationFixedTotalHours,
      unitCoordinationFixedTotalMinutesStudent: schoolParams.unitCoordinationFixedTotalMinutesStudent,
    });
    setHdrData({
      hdrSupervisorAllowance: schoolParams.hdrSupervisorAllowance,
      hdrThesisChairAllowance: schoolParams.hdrThesisChairAllowance,
    });
    setLargeUnitsStudents(schoolParams.unitCoordinationMediumUnitsStudents);
  }, [schoolParams]);

  if (isPageLoading) {
    return <Loader />;
  }

  return (
    <>
      {schoolParams !== undefined ? (
        <div className="flex w-full gap-14">
          <div className="basis-[30%] border-r border-secondary-grey-04 px-8 py-5">
            <TRDParams trdData={trdData} handleOnChange={handleOnChange} />
          </div>
          <div className="basis-[40%] border-r border-secondary-grey-04 px-8 py-5">
            <div className="text-sh3 font-bold text-primary-black-90">
              <UCParams
                ucData={ucData}
                handleOnChange={handleOnChange}
                setUpdatedSchoolParams={setUpdatedSchoolParams}
                largeUnitsStudents={largeUnitsStudents}
              />
            </div>
          </div>
          <div className="basis-[30%] px-8 py-5">
            <div className="text-sh3 font-bold text-primary-black-90">
              <HDRParams hdrData={hdrData} handleOnChange={handleOnChange} />
            </div>
          </div>
        </div>
      ) : (
        <div className="flex w-full px-8 py-5">No results</div>
      )}
    </>
  );
};
